import { Button, HStack, IconButton, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, useToast } from '@chakra-ui/react';
import { DocumentType } from '@pelicargo/types';
import { formatAwbLabel } from '@pelicargo/utils';
import { DimensionUnit, ShipmentFormPayment } from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';
import { HiPlus, HiXMark } from 'react-icons/hi2';

import { trpc, TrpcOutputs } from '../../../config/trpc';
import { useShipmentFormMutations } from '../../../hooks/shipmentForm/useShipmentFormMutations';
import { ShipmentForm } from '../../../hooks/shipmentForm/useShipmentForms';
import { SimpleDropzone } from '../../SimpleDropzone';
import { AwbInstructionForm } from './AwbInstructionForm';

type Cargo = TrpcOutputs['getCargosByRequestId'][number];

type Props = {
  mawb: ShipmentForm;
  hawbs: ShipmentForm[];
  requestId: number;
  /** Uploads must be attributed to an account; airway bills belong to the request's, as elsewhere in admin. */
  accountId?: number;
  quoteId: number;
  cargoList?: Cargo[];
  dimensionUnit?: DimensionUnit;
  hasAesFilingInBookedInlandQuote?: boolean;
  inlandQuoteVariantIdForAes?: number;
};

export const AwbInstructions = ({
  mawb,
  hawbs,
  requestId,
  accountId,
  quoteId,
  cargoList,
  dimensionUnit,
  hasAesFilingInBookedInlandQuote,
  inlandQuoteVariantIdForAes,
}: Props) => {
  const toast = useToast();
  const { createShipmentForm, updateShipmentForm, deleteShipmentForm, isCreating } = useShipmentFormMutations();
  const { mutateAsync: deleteDocument } = trpc.deleteDocument.useMutation();
  const [deletingFormId, setDeletingFormId] = useState<number | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [pendingActiveFormId, setPendingActiveFormId] = useState<number | null>(null);

  const tabs = useMemo(() => [mawb, ...hawbs], [mawb, hawbs]);

  useEffect(() => {
    if (!tabs.length) {
      setTabIndex(0);
      return;
    }

    if (pendingActiveFormId != null) {
      const pendingIndex = tabs.findIndex((form) => form.id === pendingActiveFormId);
      if (pendingIndex >= 0) {
        setTabIndex(pendingIndex);
        setPendingActiveFormId(null);
        return;
      }
    }

    setTabIndex((prev) => Math.min(prev, tabs.length - 1));
  }, [tabs, pendingActiveFormId]);

  const handleAddHawb = async () => {
    try {
      const created = await createShipmentForm({
        request_id: requestId,
        quote_id: quoteId,
        parent_shipment_form_id: mawb.id,
        payment: ShipmentFormPayment.PRE_PAID,
      });
      setPendingActiveFormId(created.id);
    } catch (error: any) {
      toast({ title: 'Could not add HAWB', description: error?.message, status: 'error' });
    }
  };

  const handleRemoveHawb = async (id: number) => {
    if (deletingFormId != null) return;
    setDeletingFormId(id);
    const removedIndex = tabs.findIndex((form) => form.id === id);

    try {
      await deleteShipmentForm({ id });

      if (removedIndex < 0) return;

      setTabIndex((prev) => {
        if (removedIndex < prev) return prev - 1;

        if (removedIndex === prev) {
          const nextTabCount = tabs.length - 1;
          return Math.min(prev, Math.max(0, nextTabCount - 1));
        }

        return prev;
      });
    } catch (error: any) {
      toast({ title: 'Could not remove HAWB', description: error?.message, status: 'error' });
    } finally {
      setDeletingFormId(null);
    }
  };

  const handleDocumentChange =
    (formId: number) =>
    async (document: { id: number } | null): Promise<void> => {
      await updateShipmentForm({
        id: formId,
        documents: document?.id ? [document.id] : [],
      });
    };

  const handleDeleteDocument =
    (formId: number) =>
    async (documentId: number): Promise<void> => {
      await deleteDocument({ id: documentId });
      await updateShipmentForm({ id: formId, documents: [] });
    };

  return (
    <Tabs variant="enclosed" colorScheme="primary" isLazy index={tabIndex} onChange={setTabIndex}>
      <TabList>
        {tabs.map((form, index) => (
          <Tab key={form.id}>
            {formatAwbLabel(index)}
            {index > 0 && (
              <IconButton
                as="span"
                aria-label="Remove HAWB"
                icon={<HiXMark />}
                size="xs"
                variant="ghost"
                ml={2}
                isLoading={deletingFormId === form.id}
                isDisabled={deletingFormId != null && deletingFormId !== form.id}
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoveHawb(form.id);
                }}
              />
            )}
          </Tab>
        ))}
        <HStack pl={2} align="center">
          <Button size="sm" variant="ghost" leftIcon={<HiPlus />} onClick={handleAddHawb} isLoading={isCreating}>
            Add HAWB
          </Button>
        </HStack>
      </TabList>
      <TabPanels>
        {tabs.map((form, index) => (
          <TabPanel key={form.id} px={0}>
            <Stack spacing={4}>
              <SimpleDropzone
                documentType={DocumentType.AIRWAY_BILL}
                requestId={requestId}
                accountId={accountId}
                documentsOverride={form.documents?.slice(0, 1)}
                onUploadComplete={handleDocumentChange(form.id)}
                onDeleteDocument={handleDeleteDocument(form.id)}
                borderStyle="dashed"
                borderRadius="lg"
                py="8"
              />
              <AwbInstructionForm
                form={form}
                cargoList={cargoList}
                dimensionUnit={dimensionUnit}
                isMawb={index === 0}
                hasHawbs={hawbs.length > 0}
                isAesFiledFromInlandQuote={index === 0 ? hasAesFilingInBookedInlandQuote : undefined}
                inlandQuoteVariantIdForAes={index === 0 ? inlandQuoteVariantIdForAes : undefined}
              />
            </Stack>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};
